import { lazyLogSigsDatapoint } from 'owa-analytics';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { AttachmentOpenAction, getAttachmentDefaultOpenAction } from 'owa-attachment-data';
import { AttachmentFullViewState } from 'owa-attachment-full-data';
import { ReferenceAttachmentModel } from 'owa-attachment-model-store';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import getAttachmentFileType from 'owa-attachment-model-store/lib/utils/getAttachmentFileType';
import { downloadAttachment } from 'owa-attachment-select-actions';
import { openInNewTab } from 'owa-attachment-user-operations';
import { ClientItemId } from 'owa-client-ids';
import { isFeatureEnabled } from 'owa-feature-flags';
import { userMailInteractionAction } from 'owa-mail-actions';
import { InstrumentationContext, selectMailStoreItemById } from 'owa-mail-store';
import Message from 'owa-service/lib/contract/Message';
import { PreviewPaneSelectionSource } from 'owa-sxs-store';
import { ImageGalleryItem } from 'owa-sxsdata';
import datapoints from '../datapoints';
import PreviewContext from '../types/PreviewContext';
import { previewAttachment } from './publicActions';

export default createLegacyActionWithDatapoint(
    'clickAttachment',
    datapoints.ClickAttachment
)(async function clickAttachment(
    viewState: AttachmentFullViewState,
    previewPaneSelectionSource: PreviewPaneSelectionSource,
    /* if instrumention context is not provided, usermailinteraction won't be logged.
    User mail interaction is specific to only search today.
    More information https://microsoft.sharepoint.com/teams/STCE/usb/_layouts/OneNote.aspx?id=%2Fteams%2FSTCE%2Fusb%2FShared%20Documents%2FCortana%20For%20Work%2Fcortana-for-work&wd=target%28Partners.one%7C229FF16A-FA84-4BFA-9C8E-A2A3FDBBD1C9%2FSearch%20Instrumentation%20With%20Execute%20Search%7CEB0BB605-BDD7-4048-8AC8-1DF427E2C9E4%2F%29 */
    instrumentationContext: InstrumentationContext,
    parentItemId: ClientItemId = null, // used if attachment is edited in SxS - to load draft into compose and to scroll to item on reading pane
    isDraft: boolean = false, // used to determine if attachment can be edited in SxS
    onBeforePreview?: () => void,
    createImageGallery?: () => Promise<ImageGalleryItem[]>,
    container?: HTMLElement
) {
    const attachmentModel = getAttachment(viewState.attachmentId);

    if (!viewState.strategy) {
        return;
    }

    switch (getAttachmentDefaultOpenAction(viewState.strategy)) {
        case AttachmentOpenAction.Preview:
            const context: PreviewContext = {
                viewState,
                previewPaneSelectionSource,
                parentItemId,
                isDraft,
                isEdit: false,
                onBeforePreview,
                createImageGallery,
                instrumentationContext,
            };

            previewAttachment(context);
            userMailInteractionAction('PreviewOfAttachmentStarted', [instrumentationContext]);
            break;
        case AttachmentOpenAction.Download:
            downloadAttachment(viewState.attachmentId, viewState.isCloudy, container);
            if (isFeatureEnabled('doc-attachment-sigs')) {
                const fileType = getAttachmentFileType(attachmentModel);
                const itemId = parentItemId?.Id;
                const message = (itemId && selectMailStoreItemById(itemId)) as Message;
                lazyLogSigsDatapoint.importAndExecute('SavedAttachment', {
                    itemId: parentItemId?.Id,
                    customProperties: {
                        FileId: viewState.attachmentId?.Id,
                        FileType: fileType,
                        Destination: 'Local',
                        Sender: (message?.From || message?.Sender)?.Mailbox?.EmailAddress,
                    },
                });
            }
            break;
        case AttachmentOpenAction.OpenInNewTab:
            const refAttachmentModel = attachmentModel as ReferenceAttachmentModel;
            if (refAttachmentModel.openUrl) {
                openInNewTab(refAttachmentModel.openUrl);
                userMailInteractionAction('OpenedAnAttachment', [instrumentationContext]);
                lazyLogSigsDatapoint.importAndExecute('OpenedAnAttachment', {
                    itemId: parentItemId?.Id,
                    customProperties: { FileId: viewState.attachmentId?.Id },
                });
            }
            break;
        default:
            break;
    }
});
